import cn from 'clsx'
import { Avatar, AvatarFallback, AvatarImage } from '../avatar'
import { type TWaveformCommentsProps, type TWaveformCommentView } from './WaveformComments.types'
import styles from './WaveformComments.module.css'

const MAX_DISPLAY_REPLIES = 99

type TMarkerProps = Pick<TWaveformCommentsProps, 'resize'> & {
    comment: TWaveformCommentView
}

const WaveformCommentMarker = ({ comment, resize }: TMarkerProps) => {
    const { imageUrl, alt, badgeCount = 0 } = comment
    const repliesCount = Math.min(badgeCount, MAX_DISPLAY_REPLIES)

    return (
        <div className={styles.marker}>
            <Avatar className={styles.avatar}>
                {imageUrl ? (
                    <AvatarImage src={imageUrl} alt={alt} width={24} height={24} resize={resize} />
                ) : null}
                <AvatarFallback />
            </Avatar>
            {badgeCount > 0 ? (
                <div className={styles.badge}>
                    <span className={styles.badgeCount}>{repliesCount}</span>
                </div>
            ) : null}
        </div>
    )
}

/**
 * Presentational overlay of comment markers pinned to a timeline (the audio page shows it over the
 * waveform). Pure: positions/labels/hrefs arrive already resolved; the screen maps comments → views.
 */
export const WaveformComments = ({ comments, className, resize }: TWaveformCommentsProps) => (
    <ul className={cn(styles.list, className)}>
        {comments.map(comment => (
            <li
                key={comment.id}
                className={styles.item}
                style={{ left: `${comment.positionPercent}%` }}
            >
                {comment.href ? (
                    <a href={comment.href} className={styles.link} aria-label={comment.alt}>
                        <WaveformCommentMarker comment={comment} resize={resize} />
                    </a>
                ) : (
                    <span role="img" aria-label={comment.alt} className={styles.link}>
                        <WaveformCommentMarker comment={comment} resize={resize} />
                    </span>
                )}
            </li>
        ))}
    </ul>
)
